import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserLogin } from '../../common/types';

// Define the context type
type UserContextType = {
  user: UserLogin | null;
  setUser: (user: UserLogin) => void;
  clearUser: () => void;
};

const storedUser = localStorage.getItem('user');
const defaultUser = storedUser ? JSON.parse(storedUser) : undefined;
// Create the UserContext with default values
const UserContext = createContext<UserContextType>({
  user: defaultUser,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setUser: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  clearUser: () => {},
});

// Helper hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// UserProvider component to wrap the app
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<UserLogin | null>(defaultUser);

  // Save user to localStorage
  const setUser = (user: UserLogin) => {
    localStorage.setItem('user', JSON.stringify(user));
    setUserState(user);
  };

  // Clear user from state and localStorage
  const clearUser = () => {
    localStorage.removeItem('user');
    setUserState(null);
  };

  // Load user from localStorage when the component mounts
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUserState(JSON.parse(storedUser));
    }
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser, clearUser }}>{children}</UserContext.Provider>
  );
};
